import React, { useEffect } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { Field, ErrorMessage, useFormikContext } from "formik";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import useLoanCalculations from "../hooks/useLoanCalculations";
import { DATE_FORMAT } from "../../../helpers/dateFormat";

const LoanDetailsStep = ({ disabled, bankAccounts }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched, touched, errors } = useFormikContext();

  const {
    calculateFixedInterest,
    calculateMonthlyInstallment,
    calculateLoanEndDate,
    calculateReductibleInterest,
    handleFirstEMIDateChange,
  } = useLoanCalculations();

  const recalcAll = () => {
    setTimeout(() => {
      calculateFixedInterest();
      calculateMonthlyInstallment();
      calculateReductibleInterest();
    }, 100);
  };

  const recalcEndDateAndAll = () => {
    setTimeout(() => {
      const newEndDate = calculateLoanEndDate();
      if (newEndDate) {
        setFieldValue("loanEndDate", newEndDate, false);
      }
      calculateFixedInterest();
      calculateMonthlyInstallment();
      calculateReductibleInterest();
    }, 100);
  };

  useEffect(() => {
    const newEndDate = calculateLoanEndDate();
    if (!newEndDate) return;
    const current = values.loanEndDate;
    if (!current || current.getTime() !== newEndDate.getTime()) {
      setFieldValue("loanEndDate", newEndDate, false);
    }
    // calculateLoanEndDate is derived from start date and duration only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.loanStartDate, values.durationYears, values.durationMonths, values.firstEMIDate]);

  return (
    <div>
      <h5 className="mb-3">{t("customer.add.steps.step3")}</h5>

      {/* Loan Amount & Interest Rate */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step3.loanAmount")}</Form.Label>
            <Field
              name="loanAmount"
              type="number"
              disabled={disabled}
              className={`form-control ${touched.loanAmount && errors.loanAmount ? "is-invalid" : ""}`}
              step="0.01"
              min="0"
              onChange={(e) => {
                setFieldValue("loanAmount", e.target.value);
                recalcAll();
              }}
            />
            <ErrorMessage name="loanAmount" component="div" className="invalid-feedback d-block" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step3.interestPerYear")}</Form.Label>
            <Field
              name="interestPerYear"
              type="number"
              disabled={disabled}
              className={`form-control ${touched.interestPerYear && errors.interestPerYear ? "is-invalid" : ""}`}
              step="0.01"
              min="0"
              max="100"
              onChange={(e) => {
                setFieldValue("interestPerYear", e.target.value);
                recalcAll();
              }}
            />
            <ErrorMessage name="interestPerYear" component="div" className="invalid-feedback d-block" />
          </Form.Group>
        </Col>
      </Row>

      {/* Start Date & Duration */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step3.loanStartDate")}</Form.Label>
            <div>
              <DatePicker
                name="loanStartDate"
                wrapperClassName="w-100"
                selected={values.loanStartDate}
                onChange={(date) => {
                  setFieldValue("loanStartDate", date);
                  setTimeout(() => {
                    const newEndDate = calculateLoanEndDate();
                    if (newEndDate) setFieldValue("loanEndDate", newEndDate, false);
                  }, 100);
                }}
                dateFormat={DATE_FORMAT.DISPLAY}
                className={`form-control ${touched.loanStartDate && errors.loanStartDate ? "is-invalid" : ""}`}
                disabled={disabled}
              />
            </div>
            <ErrorMessage name="loanStartDate" component="div" className="invalid-feedback d-block" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step3.duration")}</Form.Label>
            <Row>
              <Col md={6}>
                <Field
                  name="durationYears"
                  type="number"
                  disabled={disabled}
                  className={`form-control ${touched.durationYears && errors.durationYears ? "is-invalid" : ""}`}
                  placeholder={t("customer.add.step3.years")}
                  min="0"
                  onChange={(e) => {
                    setFieldValue("durationYears", e.target.value);
                    recalcEndDateAndAll();
                  }}
                />
                <ErrorMessage name="durationYears" component="div" className="invalid-feedback d-block" />
              </Col>
              <Col md={6}>
                <Field
                  name="durationMonths"
                  type="number"
                  disabled={disabled}
                  className={`form-control ${touched.durationMonths && errors.durationMonths ? "is-invalid" : ""}`}
                  placeholder={t("customer.add.step3.months")}
                  min="0"
                  max="11"
                  onChange={(e) => {
                    setFieldValue("durationMonths", e.target.value);
                    recalcEndDateAndAll();
                  }}
                />
                <ErrorMessage name="durationMonths" component="div" className="invalid-feedback d-block" />
              </Col>
            </Row>
          </Form.Group>
        </Col>
      </Row>

      {/* Loan End Date */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t("customer.add.step3.loanEndDate")}</Form.Label>
            <div>
              <DatePicker
                wrapperClassName="w-100"
                selected={calculateLoanEndDate() || values.loanEndDate}
                onChange={() => { }}
                dateFormat={DATE_FORMAT.DISPLAY}
                className="form-control bg-light text-muted"
                disabled
                readOnly
                style={{ opacity: 1 }}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>

      {/* Interest Type & Monthly Installment */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step3.interestType")}</Form.Label>
            <div className="d-flex gap-3">
              <div className="form-check">
                <input
                  type="radio"
                  id="interestType-fixed"
                  name="interestType"
                  value="fixed"
                  checked={values.interestType === "fixed"}
                  onClick={() => {
                    setFieldValue("interestType", "fixed", true);
                    setTimeout(() => calculateFixedInterest(true), 100);
                  }}
                  onBlur={() => setFieldTouched("interestType", true, true)}
                  disabled={disabled}
                  className={`form-check-input ${touched.interestType && errors.interestType ? "is-invalid" : ""}`}
                />
                <label htmlFor="interestType-fixed" className="form-check-label">
                  {t("customer.add.step3.fixedInterest")}
                </label>
              </div>
              <div className="form-check">
                <input
                  type="radio"
                  id="interestType-reductible"
                  name="interestType"
                  value="reductible"
                  checked={values.interestType === "reducing"}
                  onClick={() => {
                    setFieldValue("interestType", "reducing", true);
                    setFieldTouched("interestType", true, true);
                    setTimeout(() => {
                      setFieldValue("monthlyInstallment", true, true);
                      const newEndDate = calculateLoanEndDate();
                      if (newEndDate) setFieldValue("loanEndDate", newEndDate, false);
                      calculateReductibleInterest(true);
                    }, 100);
                  }}
                  onBlur={() => setFieldTouched("interestType", true, true)}
                  disabled={disabled}
                  className={`form-check-input ${touched.interestType && errors.interestType ? "is-invalid" : ""}`}
                />
                <label htmlFor="interestType-reductible" className="form-check-label">
                  {t("customer.add.step3.reductibleInterest")}
                </label>
              </div>
            </div>
            <ErrorMessage name="interestType" component="div" className="invalid-feedback d-block" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <div className="form-check">
              <input
                type="checkbox"
                id="monthlyInstallment"
                name="monthlyInstallment"
                checked={values.monthlyInstallment}
                onClick={() => {
                  const newValue = !values.monthlyInstallment;
                  setFieldValue("monthlyInstallment", newValue, true);
                  setFieldTouched("monthlyInstallment", true, true);
                  setTimeout(() => {
                    const newEndDate = calculateLoanEndDate();
                    if (newEndDate) setFieldValue("loanEndDate", newEndDate, false);
                    calculateMonthlyInstallment(newValue);
                    if (!newValue) {
                      setFieldValue("monthlyInstallmentAmount", "", false);
                      setFieldValue("firstEMIDate", null, false);
                    }
                  }, 100);
                }}
                onBlur={() => setFieldTouched("monthlyInstallment", true, true)}
                disabled={disabled}
                className="form-check-input"
              />
              <label htmlFor="monthlyInstallment" className="form-check-label">
                {t("customer.add.step3.monthlyInstallment")}
              </label>
            </div>
          </Form.Group>
          <Form.Group>
            <div className="form-check">
              <input
                type="checkbox"
                id="reminder"
                name="reminder"
                onChange={(e) => {
                  setFieldValue("reminder", e.target.checked, true);
                  setFieldTouched("reminder", true, true);
                }}
                onBlur={() => setFieldTouched("reminder", true, true)}
                disabled={disabled}
                className="form-check-input"
              />
              <label htmlFor="reminder" className="form-check-label">
                {t("customer.add.step3.reminder")}
              </label>
            </div>
          </Form.Group>
        </Col>
      </Row>

      {/* Total Interest & Total Payable */}
      {(values.interestType === "fixed" || values.interestType === "reducing") && (
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t("customer.add.step3.totalInterest")}</Form.Label>
              <Field name="totalInterest" type="number" disabled className="form-control bg-light" step="0.01" min="0" />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t("customer.add.step3.totalAmountPayable")}</Form.Label>
              <Field name="totalAmountPayable" type="number" disabled className="form-control bg-light" step="0.01" min="0" />
            </Form.Group>
          </Col>
        </Row>
      )}

      {/* First EMI Date & Monthly Amount */}
      {values.monthlyInstallment && (
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="required">{t("customer.add.step3.firstEMIDate")}</Form.Label>
              <div>
                <DatePicker
                  name="firstEMIDate"
                  selected={values.firstEMIDate}
                  wrapperClassName="w-100"
                  onChange={(date) => handleFirstEMIDateChange(date)}
                  onBlur={() => setFieldTouched("firstEMIDate", true, true)}
                  dateFormat={DATE_FORMAT.DISPLAY}
                  className={`form-control w-100 ${touched.firstEMIDate && errors.firstEMIDate ? "is-invalid" : ""}`}
                  disabled={disabled}
                  minDate={values.loanStartDate || new Date()}
                />
              </div>
              <ErrorMessage name="firstEMIDate" component="div" className="invalid-feedback d-block" />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t("customer.add.step3.monthlyInstallmentAmount")}</Form.Label>
              <Field name="monthlyInstallmentAmount" type="number" disabled className="form-control bg-light" step="0.01" min="0" />
            </Form.Group>
          </Col>
        </Row>
      )}

      {values?.monthlyInstallment && values?.totalInstallments && (
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t("customer.add.step3.totalInstallments")}</Form.Label>
              <Field
                name="totalInstallments"
                type="number"
                disabled
                readOnly
                className="form-control bg-light text-muted"
                min="0"
                style={{ opacity: 1 }}
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      {/* Processing Charge & Payment Type */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t("customer.add.step3.processingCharge")}</Form.Label>
            <Field name="processingCharge" type="number" disabled={disabled} className="form-control" step="0.01" min="0" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step3.paymentType")}</Form.Label>
            <div>
              <input
                type="radio"
                id="paymentType-cash"
                name="paymentType"
                value="cash"
                checked={values.paymentType === "cash"}
                onClick={() => {
                  setFieldValue("paymentType", "cash", true);
                  setFieldTouched("paymentType", true, true);
                }}
                onBlur={() => setFieldTouched("paymentType", true, true)}
                disabled={disabled}
                className={`form-check-input me-2 ${touched.paymentType && errors.paymentType ? "is-invalid" : ""}`}
              />
              <label htmlFor="paymentType-cash" className="form-check-label me-3">
                {t("customer.add.step3.cash")}
              </label>
              <input
                type="radio"
                id="paymentType-bank"
                name="paymentType"
                value="bank"
                checked={values.paymentType === "bank"}
                onClick={() => {
                  setFieldValue("paymentType", "bank", true);
                  setFieldTouched("paymentType", true, true);
                }}
                onBlur={() => setFieldTouched("paymentType", true, true)}
                disabled={disabled}
                className={`form-check-input me-2 ${touched.paymentType && errors.paymentType ? "is-invalid" : ""}`}
              />
              <label htmlFor="paymentType-bank" className="form-check-label">
                {t("customer.add.step3.bank")}
              </label>
            </div>
            <ErrorMessage name="paymentType" component="div" className="invalid-feedback d-block" />
          </Form.Group>
        </Col>
      </Row>

      {/* Processing Charge Received Mode */}
      {values.processingCharge && values.processingCharge !== "" && parseFloat(values.processingCharge) > 0 && (
        <>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">{t("customer.add.step3.processingChargeReceivedMode")}</Form.Label>
                <div>
                  <input
                    type="radio"
                    id="processingChargeReceivedMode-cash"
                    name="processingChargeReceivedMode"
                    value="cash"
                    checked={values.processingChargeReceivedMode === "cash"}
                    onClick={() => {
                      setFieldValue("processingChargeReceivedMode", "cash", true);
                      setFieldTouched("processingChargeReceivedMode", true, true);
                      setFieldValue("processingChargeBankAccount", "", false);
                      setFieldValue("processingChargeTransactionNo", "", false);
                    }}
                    onBlur={() => setFieldTouched("processingChargeReceivedMode", true, true)}
                    disabled={disabled}
                    className={`form-check-input me-2 ${touched.processingChargeReceivedMode && errors.processingChargeReceivedMode ? "is-invalid" : ""}`}
                  />
                  <label htmlFor="processingChargeReceivedMode-cash" className="form-check-label me-3">
                    {t("customer.add.step3.cash")}
                  </label>
                  <input
                    type="radio"
                    id="processingChargeReceivedMode-bank"
                    name="processingChargeReceivedMode"
                    value="bank"
                    checked={values.processingChargeReceivedMode === "bank"}
                    onClick={() => {
                      setFieldValue("processingChargeReceivedMode", "bank", true);
                      setFieldTouched("processingChargeReceivedMode", true, true);
                    }}
                    onBlur={() => setFieldTouched("processingChargeReceivedMode", true, true)}
                    disabled={disabled}
                    className={`form-check-input me-2 ${touched.processingChargeReceivedMode && errors.processingChargeReceivedMode ? "is-invalid" : ""}`}
                  />
                  <label htmlFor="processingChargeReceivedMode-bank" className="form-check-label">
                    {t("customer.add.step3.bank")}
                  </label>
                </div>
                <ErrorMessage name="processingChargeReceivedMode" component="div" className="invalid-feedback d-block" />
              </Form.Group>
            </Col>
          </Row>

          {values.processingChargeReceivedMode === "bank" && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="required">{t("customer.add.step3.processingChargeBankAccount")}</Form.Label>
                  <Field
                    as="select"
                    name="processingChargeBankAccount"
                    disabled={disabled}
                    className={`form-control ${touched.processingChargeBankAccount && errors.processingChargeBankAccount ? "is-invalid" : ""}`}
                  >
                    <option value="">Select Bank Account</option>
                    {bankAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.accountNumber} - {account.bankName}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="processingChargeBankAccount" component="div" className="invalid-feedback d-block" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="required">{t("customer.add.step3.processingChargeTransactionNo")}</Form.Label>
                  <Field
                    name="processingChargeTransactionNo"
                    disabled={disabled}
                    className={`form-control ${touched.processingChargeTransactionNo && errors.processingChargeTransactionNo ? "is-invalid" : ""}`}
                  />
                  <ErrorMessage name="processingChargeTransactionNo" component="div" className="invalid-feedback d-block" />
                </Form.Group>
              </Col>
            </Row>
          )}
        </>
      )}

      {/* Bank Account Selection for Payment */}
      {values.paymentType === "bank" && (
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="required">{t("customer.add.step3.selectBankAccount")}</Form.Label>
              <Field
                as="select"
                name="bankAccount"
                disabled={disabled}
                className={`form-control ${touched.bankAccount && errors.bankAccount ? "is-invalid" : ""}`}
              >
                <option value="">Select Bank Account</option>
                {bankAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.accountNumber} - {account.bankName}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="bankAccount" component="div" className="invalid-feedback d-block" />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="required">{t("customer.add.step3.chequeTransactionNo")}</Form.Label>
              <Field
                name="chequeTransactionNo"
                disabled={disabled}
                className={`form-control ${touched.chequeTransactionNo && errors.chequeTransactionNo ? "is-invalid" : ""}`}
              />
              <ErrorMessage name="chequeTransactionNo" component="div" className="invalid-feedback d-block" />
            </Form.Group>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default LoanDetailsStep;
